/**
 * Homónimos de destino (módulo puro — sem IA, sem Maps, sem créditos).
 *
 * Regra genérica: quando o nome escrito existe em vários países (Caracal, RO
 * vs. Caracal, MX) — ou em vários sítios do mesmo país — perguntamos qual é,
 * de graça, ANTES de gastar IA/Maps/crédito. Nunca escolhemos por adivinhação.
 */

import { comparableCityName } from "./city-endonym";
import { classifyPlaceKind, type DestinationCategory } from "./destination-class";


export type RawDestinationCandidate = {
  name?: string | null;
  /** Localidade/cidade normalizada, quando conhecida. */
  locality?: string | null;
  region?: string | null;
  county?: string | null;
  country?: string | null;
  countryCode?: string | null;
  lat?: number | null;
  lng?: number | null;
  /** Importância/relevância devolvida pela fonte (0–1). */
  importance?: number | null;
  kind?: string | null;
  /** Identificador estável da fonte, quando esta o disponibiliza. */
  placeId?: string | null;
};

export type DestinationOption = {
  id: string;
  /** Nome a mostrar (a localidade pedida, nunca o município substituto). */
  name: string;
  region?: string;
  country: string;
  countryCode: string;
  lat: number;
  lng: number;
  /** Identificador canónico da fonte; coordenadas continuam vinculativas. */
  placeId?: string;
  /** "Caracal, Olt, Roménia" */
  label: string;
  importance: number;
  /** Tipo bruto devolvido pelo geocodificador ("city", "island", ...). */
  kind?: string;
  /** Categoria semântica do lugar. */
  category: DestinationCategory;
};


const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .trim();

/** Tipos de lugar aceitáveis como destino de um roteiro. */
const PLACE_KINDS = new Set([
  "city",
  "town",
  "village",
  "municipality",
  "administrative",
  "borough",
  "suburb",
  "hamlet",
  "locality",
  "county",
  "state",
  "province",
  "island",
  "islet",
  "archipelago",
  "state_district",
  "region",
  "territory",

]);

/**
 * Prefixos administrativos que o geocodificador acrescenta ao nome da cidade
 * ("Grande Londres", "Cidade de Londres", "Greater London", "City of London").
 * São removidos antes de comparar e de apresentar.
 */
const ADMIN_PREFIXES = [
  "grande",
  "gran",
  "grand",
  "greater",
  "cidade de",
  "ciudad de",
  "city of",
  "ville de",
  "citta di",
  "stadt",
  "municipio de",
  "concelho de",
];

const ADMIN_SUFFIXES = ["city", "cidade", "ciudad"];

/** Nome da localidade sem prefixos/sufixos administrativos. */
export function stripAdministrativePrefix(name: string): string {
  let out = (name ?? "").trim();
  for (const prefix of ADMIN_PREFIXES) {
    const pattern = new RegExp(`^${prefix}\\s+`, "iu");
    const folded = fold(out);
    if (folded.startsWith(`${prefix} `)) {
      out = out.replace(pattern, "").trim();
      break;
    }
  }
  for (const suffix of ADMIN_SUFFIXES) {
    if (fold(out).endsWith(` ${suffix}`) && fold(out) !== suffix) {
      out = out.slice(0, out.length - suffix.length - 1).trim();
      break;
    }
  }
  return out;
}

/** Chave comparável entre exónimos ("Londres"/"London", "Atenas"/"Athens"). */
const aliasKey = (value: string) => fold(comparableCityName(stripAdministrativePrefix(value ?? "")));

export function normalizeHomonymCandidates(
  raw: RawDestinationCandidate[] | null | undefined,
  requestedName: string,
): DestinationOption[] {
  const wanted = aliasKey(requestedName);
  const written = stripAdministrativePrefix((requestedName ?? "").trim());
  const out: DestinationOption[] = [];
  const seen = new Set<string>();
  for (const item of raw ?? []) {
    const lat = Number(item?.lat);
    const lng = Number(item?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (item?.kind && !PLACE_KINDS.has(String(item.kind))) continue;
    // "Lagos, Portugal" e "Lagos" são o mesmo homónimo: o nome é o 1.º segmento.
    const rawName = (item.locality || item.name || "").split(",")[0].trim();
    const resolvedName = stripAdministrativePrefix(rawName);

    if (!resolvedName) continue;
    // Só interessam homónimos: o nome tem de corresponder ao pedido, já com
    // exónimos ("Londres"/"London") e prefixos administrativos normalizados.
    const candidateKey = aliasKey(resolvedName);
    if (
      wanted &&
      candidateKey !== wanted &&
      !candidateKey.startsWith(`${wanted} `) &&
      !fold(resolvedName).startsWith(`${fold(written)} `)
    ) {
      continue;
    }
    const country = (item.country || "").trim();
    const countryCode = (item.countryCode || "").trim().toUpperCase();
    if (!country && !countryCode) continue;
    const region = (item.region || item.county || "").trim() || undefined;
    // Nome apresentado: o que o utilizador escreveu, quando é o mesmo sítio.
    const name = written && candidateKey === wanted ? written : resolvedName;
    const placeId = item.placeId?.trim() || undefined;
    // Identidades diferentes (fornecedor + ID) são destinos diferentes, mesmo
    // dentro do mesmo país. Sem ID, as coordenadas fazem de identidade.
    const key =
      placeId ??
      `${fold(name)}|${countryCode || fold(country)}|${fold(region ?? "")}|${lat.toFixed(3)},${lng.toFixed(3)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: key,
      name,
      region,
      country,
      countryCode,
      lat,
      lng,
      placeId,
      label: [name, region, country].filter(Boolean).join(", "),
      importance: Number(item.importance) || 0,
      kind: item.kind ? String(item.kind) : undefined,
      category: classifyPlaceKind(item.kind),
    });
  }
  return groupSemanticDuplicates(out).slice(0, 5);
}

/** Distância aproximada em km entre dois pontos (suficiente para agrupar). */
function distanceKm(a: DestinationOption, b: DestinationOption): number {
  const dLat = (a.lat - b.lat) * 111;
  const dLng = (a.lng - b.lng) * 111 * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
  return Math.hypot(dLat, dLng);
}

/** Quanto maior, melhor representante do sítio para um roteiro turístico. */
function representativeRank(option: DestinationOption): number {
  const kind = (option.kind || "").toLowerCase();
  if (["city", "town", "village", "hamlet", "borough", "locality"].includes(kind)) return 4;
  if (kind === "municipality") return 3;
  if (["island", "province", "state", "county", "region"].includes(kind)) return 2;
  if (kind === "administrative") return 1;
  return 2;
}

/** Limiar de sobreposição: centros a esta distância são o mesmo destino. */
const SAME_PLACE_KM = 30;

/** Níveis de localidade (o que uma rota urbana realmente visita). */
const LOCALITY_KINDS = new Set([
  "city",
  "town",
  "village",
  "hamlet",
  "borough",
  "locality",
  "municipality",
  "suburb",
]);

/** Níveis administrativos mais largos que contêm a localidade homónima. */
const BROADER_KINDS = new Set([
  "state",
  "province",
  "region",
  "county",
  "state_district",
  "territory",
  "island",
  "archipelago",
  "administrative",
]);

const kindOf = (option: DestinationOption) => (option.kind || "").toLowerCase().trim();

/**
 * Mesmo nome, mesmo país, mas níveis diferentes da mesma hierarquia
 * (Nova Iorque cidade vs. Nova Iorque estado; Luxemburgo cidade vs. país):
 * é UM destino, e a rota visita a localidade. Regra genérica, sem exceções
 * por nome — nunca junta duas localidades homónimas distintas.
 */
function sameHierarchyLevelPair(a: DestinationOption, b: DestinationOption): boolean {
  const ka = kindOf(a);
  const kb = kindOf(b);
  if (!ka || !kb || ka === kb) return false;
  return (
    (LOCALITY_KINDS.has(ka) && BROADER_KINDS.has(kb)) ||
    (LOCALITY_KINDS.has(kb) && BROADER_KINDS.has(ka))
  );
}

/**
 * Camada semântica: o mesmo destino turístico devolvido como cidade,
 * município, freguesia ou limite administrativo é UMA opção só. Só ficam
 * separados locais realmente distintos (país diferente ou centros afastados).
 */
export function groupSemanticDuplicates(options: DestinationOption[]): DestinationOption[] {
  const groups: DestinationOption[][] = [];
  for (const option of options) {
    const group = groups.find((candidates) => {
      const head = candidates[0];
      if (aliasKey(head.name) !== aliasKey(option.name)) return false;
      const sameCountry =
        (head.countryCode || fold(head.country)) === (option.countryCode || fold(option.country));
      if (!sameCountry) return false;
      // Mesmo nome, mesmo país e mesma região = níveis administrativos do
      // mesmo sítio (cidade vs concelho), mesmo que os centroides difiram.
      const sameRegion = Boolean(head.region && option.region) && fold(head.region!) === fold(option.region!);
      if (sameRegion) return true;
      // Localidade vs. nível mais largo com o mesmo nome: mesma hierarquia.
      if (candidates.some((candidate) => sameHierarchyLevelPair(candidate, option))) return true;
      return distanceKm(head, option) <= SAME_PLACE_KM;
    });
    if (group) group.push(option);
    else groups.push([option]);
  }

  const picked = groups.map((group) => {
    const sorted = [...group].sort(
      (a, b) => representativeRank(b) - representativeRank(a) || b.importance - a.importance,
    );
    const best = sorted[0];
    // A importância do grupo é a melhor de todas as fontes do mesmo sítio.
    const importance = Math.max(...group.map((item) => item.importance));
    const region = best.region || group.find((item) => item.region)?.region;
    return {
      ...best,
      region,
      importance,
      label: [best.name, region, best.country].filter(Boolean).join(", "),
    };
  });
  // Dois sítios distintos com o mesmo rótulo: explicitar a diferença real.
  const byLabel = new Map<string, number>();
  for (const option of picked) byLabel.set(option.label, (byLabel.get(option.label) ?? 0) + 1);
  const disambiguated = picked.map((option) =>
    (byLabel.get(option.label) ?? 0) > 1 && option.kind
      ? { ...option, label: `${option.label} (${option.kind})` }
      : option,
  );
  return disambiguated.sort((a, b) => b.importance - a.importance);
}


/**
 * Se o texto já nomeia um dos países (ou o código ISO), a escolha está feita:
 * restringimos aos candidatos desse país.
 */
export function filterByPromptCountry(
  options: DestinationOption[],
  prompt: string,
): DestinationOption[] {
  const text = fold(prompt);
  if (!text) return options;
  const words = new Set(text.split(/[^a-z0-9]+/u).filter(Boolean));
  // País explícito ganha sempre. Não misturar depois regiões de outro
  // candidato (ex.: a região de Lagos, Nigéria, também se chama "Lagos").
  const countryMatches = options.filter((option) => {
    const country = fold(option.country);
    const countryCode = fold(option.countryCode);
    return (country.length > 2 && text.includes(country)) || words.has(countryCode);
  });
  if (countryMatches.length > 0) return countryMatches;
  const regionMatches = options.filter((option) => {
    const region = fold(option.region ?? "");
    return region.length > 2 && region !== fold(option.name) && text.includes(region);
  });
  return regionMatches.length > 0 ? regionMatches : options;
}

export type HomonymDecision =
  | { status: "resolved"; option: DestinationOption }
  | { status: "ask"; options: DestinationOption[] }
  | { status: "none" };

export function resolveHomonyms(
  raw: RawDestinationCandidate[] | null | undefined,
  requestedName: string,
  prompt: string,
): HomonymDecision {
  const all = normalizeHomonymCandidates(raw, requestedName);
  if (all.length === 0) return { status: "none" };
  const scoped = filterByPromptCountry(all, prompt);
  if (scoped.length === 1) return { status: "resolved", option: scoped[0] };
  return { status: "ask", options: scoped };
}

const COPY: Record<
  string,
  { question: (name: string) => string; note: string; other: string }
> = {
  pt: {
    question: (name) => `Qual ${name} queres visitar?`,
    note: "Ainda não usámos créditos — escolhe o local certo e seguimos.",
    other: "Outro (escrever)",
  },
  en: {
    question: (name) => `There is more than one place called "${name}". Which one?`,
    note: "No credit used yet — pick the right place and we continue.",
    other: "Other (type it)",
  },
  es: {
    question: (name) => `Hay más de un lugar llamado "${name}". ¿Cuál quieres?`,
    note: "Aún no usamos créditos — elige el lugar correcto y seguimos.",
    other: "Otro (escribir)",
  },
  fr: {
    question: (name) => `Il existe plusieurs lieux nommés « ${name} ». Lequel ?`,
    note: "Aucun crédit utilisé — choisis le bon lieu et on continue.",
    other: "Autre (écrire)",
  },
  it: {
    question: (name) => `Esiste più di un luogo chiamato "${name}". Quale vuoi?`,
    note: "Nessun credito usato — scegli il posto giusto e proseguiamo.",
    other: "Altro (scrivere)",
  },
  de: {
    question: (name) => `Es gibt mehrere Orte namens „${name}". Welchen meinst du?`,
    note: "Noch kein Guthaben verbraucht — wähle den richtigen Ort.",
    other: "Anderer (eingeben)",
  },
};

export function homonymCopy(language = "pt") {
  return COPY[language.slice(0, 2).toLowerCase()] ?? COPY.en;
}

/**
 * Reescreve o pedido com o local escolhido ("Caracal" → "Caracal, Olt,
 * Roménia") para que a geração já não tenha ambiguidade.
 */
export function applyDestinationChoice(
  prompt: string,
  requestedName: string,
  option: DestinationOption,
): string {
  const text = prompt ?? "";
  const escaped = requestedName.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
  const pattern = new RegExp(escaped, "iu");
  if (!pattern.test(text)) return `${text.trim()} (${option.label})`.trim();
  return text.replace(pattern, option.label);
}
